/* eslint-disable react/prop-types */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Typography,
  Sheet,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import Link from '@mui/joy/Link';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../../../main/widgets/CustomTextField';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import {
  createSettlementGroup,
  getSettlementGroup,
  updateSettlementGroup,
} from '../../../main/API/settlementGroup';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';
import {
  buildSettlementGroupPayload,
  emptySettlementGroupForm,
  isDefaultSettlementGroup,
  settlementGroupApiValidationFeedback,
  settlementGroupFromApi,
  validateSettlementGroupClient,
} from './settlementGroupFormPayload';

const FIELD_SCROLL_ORDER = [
  'code',
  'name',
  'name_ar',
  'description',
  'description_ar',
  'separate_checkout',
  'sort_order',
  'active',
];

function scrollToFirstFieldError(errors, banner) {
  const errMap = errors && typeof errors === 'object' ? errors : {};
  const tryScroll = (el) => {
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector(
      'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') {
      try {
        focusable.focus({ preventScroll: true });
      } catch (_e) {
        focusable.focus();
      }
    }
    return true;
  };

  for (let i = 0; i < FIELD_SCROLL_ORDER.length; i++) {
    const key = FIELD_SCROLL_ORDER[i];
    if (errMap[key]) {
      const el = document.getElementById(`settlement-group-field-${key}`);
      if (tryScroll(el)) return;
    }
  }
  if (banner) {
    const el = document.getElementById('settlement-group-save-error');
    if (tryScroll(el)) return;
  }
}

/**
 * Shared create/edit form for SettlementGroup master data.
 */
export default function SettlementGroupForm({
  mode,
  groupId,
  onSaved,
  hideBreadcrumbs = false,
  embed = false,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptySettlementGroupForm);
  const [fetching, setFetching] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const pendingScrollToErrorsRef = useRef(false);

  const isDefault = isDefaultSettlementGroup(form);
  const canSubmit =
    mode === 'create'
      ? hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_CREATE)
      : hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_UPDATE);

  useEffect(() => {
    if (mode !== 'edit' || !groupId) {
      setFetching(false);
      return;
    }
    if (!hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_VIEW)) {
      setLoadError(t('permissions.noPermission', { defaultValue: 'Permission denied.' }));
      setFetching(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    getSettlementGroup(groupId)
      .then((res) => {
        if (cancelled) return;
        setForm(settlementGroupFromApi(res.data));
        setLoadError('');
      })
      .catch((e) => {
        if (!cancelled) setLoadError(masterApiErrorMessage(e, t));
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, groupId, hasPermission, t]);

  useLayoutEffect(() => {
    if (!pendingScrollToErrorsRef.current) return;
    pendingScrollToErrorsRef.current = false;
    scrollToFirstFieldError(fieldErrors, saveError);
  }, [fieldErrors, saveError]);

  const patch = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');
    const v = validateSettlementGroupClient(form, t, { isCreate: mode === 'create' });
    setFieldErrors(v);
    if (Object.keys(v).length) {
      pendingScrollToErrorsRef.current = true;
      return;
    }
    if (!canSubmit) return;
    // Protect seeded default: keep active=true even if UI was tampered with.
    const payload = buildSettlementGroupPayload(
      isDefault ? { ...form, active: true } : form
    );
    setSaving(true);
    try {
      if (mode === 'create') {
        const res = await createSettlementGroup(payload);
        if (typeof onSaved === 'function') {
          onSaved(res.data);
        } else {
          navigate('/settings/settlementgroups', { state: { settlementGroupJustCreated: true } });
        }
      } else {
        await updateSettlementGroup(groupId, payload);
        if (typeof onSaved === 'function') {
          onSaved(payload);
        } else {
          navigate('/settings/settlementgroups', { state: { settlementGroupJustUpdated: true } });
        }
      }
    } catch (err) {
      const { fieldErrors: apiFieldErrors, saveError: apiBanner } =
        settlementGroupApiValidationFeedback(err, t);
      setFieldErrors(apiFieldErrors);
      setSaveError(apiBanner);
      if (Object.keys(apiFieldErrors).length > 0 || apiBanner) {
        pendingScrollToErrorsRef.current = true;
      }
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create'
      ? t('settlementGroups.formAddTitle')
      : t('settlementGroups.formEditTitle');

  return (
    <Box sx={embed ? undefined : { m: 3 }}>
      {!hideBreadcrumbs && (
        <Box mb={2}>
          <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
            <Link color="neutral" href="/settings/settlementgroups">
              {t('nav.settlementGroups')}
            </Link>
            <Typography>{title}</Typography>
          </Breadcrumbs>
          <Typography level="h1" textAlign="start">
            {title}
          </Typography>
        </Box>
      )}

      {loadError ? (
        <Typography color="danger" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      ) : null}

      {fetching ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            gap: 2,
          }}
        >
          <CircularProgress size="lg" />
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            {t('actions.loading')}
          </Typography>
        </Box>
      ) : (
        <Sheet
          variant="outlined"
          sx={{ p: 3, borderRadius: 'md', bgcolor: 'background.surface' }}
          component="form"
          onSubmit={handleSubmit}
        >
          {saveError ? (
            <Typography id="settlement-group-save-error" color="danger" sx={{ mb: 2 }}>
              {saveError}
            </Typography>
          ) : null}

          {isDefault ? (
            <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
              {t('settlementGroups.defaultProtectedHint')}
            </Alert>
          ) : null}

          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('settlementGroups.sectionIdentity')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={4} id="settlement-group-field-code">
              <CustomTextField
                label={t('settlementGroups.fieldCode')}
                hintTooltip={t('settlementGroups.hintCode')}
                value={form.code}
                onChange={(e) => patch('code', e.target.value)}
                required
                disabled={mode === 'edit' || saving}
                fullWidth
              />
              {fieldErrors.code ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.code}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={4} id="settlement-group-field-name">
              <CustomTextField
                label={t('settlementGroups.fieldName')}
                value={form.name}
                onChange={(e) => patch('name', e.target.value)}
                required
                disabled={saving}
                fullWidth
              />
              {fieldErrors.name ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.name}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={4} id="settlement-group-field-name_ar">
              <CustomTextField
                label={t('settlementGroups.fieldNameAr')}
                value={form.name_ar}
                onChange={(e) => patch('name_ar', e.target.value)}
                disabled={saving}
                fullWidth
              />
              {fieldErrors.name_ar ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.name_ar}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={6} id="settlement-group-field-description">
              <CustomTextField
                label={t('settlementGroups.fieldDescription')}
                value={form.description}
                onChange={(e) => patch('description', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={6} id="settlement-group-field-description_ar">
              <CustomTextField
                label={t('settlementGroups.fieldDescriptionAr')}
                value={form.description_ar}
                onChange={(e) => patch('description_ar', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {t('settlementGroups.sectionCheckout')}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
            {t('settlementGroups.sectionCheckoutHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} id="settlement-group-field-separate_checkout">
              <NativeCheckbox
                checked={form.separate_checkout}
                onChange={(e) => patch('separate_checkout', e.target.checked)}
                label={t('settlementGroups.fieldSeparateCheckout')}
                disabled={saving}
              />
            </Grid>
            <Grid xs={12} sm={6} id="settlement-group-field-active">
              <NativeCheckbox
                checked={form.active}
                onChange={(e) => patch('active', e.target.checked)}
                label={t('settlementGroups.fieldActive')}
                disabled={saving || isDefault}
              />
            </Grid>
            <Grid xs={12} md={4} id="settlement-group-field-sort_order">
              <CustomTextField
                label={t('settlementGroups.fieldSortOrder')}
                type="number"
                value={form.sort_order}
                onChange={(e) => patch('sort_order', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            <PermissionGate
              permission={
                mode === 'create'
                  ? PERMISSIONS.SETTLEMENT_GROUPS_CREATE
                  : PERMISSIONS.SETTLEMENT_GROUPS_UPDATE
              }
            >
              <Button type="submit" loading={saving} disabled={!canSubmit || saving}>
                {mode === 'create' ? t('actions.submit') : t('actions.save')}
              </Button>
            </PermissionGate>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => navigate('/settings/settlementgroups')}
              disabled={saving}
            >
              {t('settlementGroups.backToList')}
            </Button>
          </Box>
        </Sheet>
      )}

    </Box>
  );
}
